import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle2,
  Loader2,
  Clock,
  Copy,
  Check,
  Database,
  Table2,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { AgentResponse } from './AgentResponse';

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      className="h-5 w-5 absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center hover:bg-slate-700"
      onClick={(e) => { e.stopPropagation(); handleCopy(); }}
    >
      {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 text-gray-500" />}
    </button>
  );
};

const DataTable = ({ data }) => {
  if (!data || data.length === 0) return null;
  const columns = Object.keys(data[0]);
  
  return (
    <div className="overflow-x-auto rounded-lg bg-slate-900/60 border border-slate-800/50 mt-2">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-slate-800/30">
            {columns.map((col) => (
              <th key={col} className="px-2.5 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide border-b border-slate-800/50">
                {col.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx} className="border-b border-slate-800/30 last:border-0">
              {columns.map((col) => (
                <td key={col} className="px-2.5 py-1.5 text-gray-400 font-mono text-[11px]">
                  {col === 'completeness' ? (
                    <span className="text-emerald-400 font-medium">{row[col]}</span>
                  ) : row[col]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Circle = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
  </svg>
);

const StepItem = ({ step, status = 'pending', forceCollapse = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('query');
  const hasContent = step.hasQuery || step.output;

  // Auto-expand when in_progress
  useEffect(() => {
    if (status === 'in_progress' && hasContent) {
      setIsExpanded(true);
    }
  }, [status, hasContent]);

  // Auto-collapse when forceCollapse is true
  useEffect(() => {
    if (forceCollapse) {
      setIsExpanded(false);
    }
  }, [forceCollapse]);

  return (
    <div className="border-l border-slate-800/50 pl-3 py-1.5 ml-1">
      {/* Step header */}
      <div 
        className={`flex items-center gap-2.5 ${hasContent ? 'cursor-pointer hover:bg-slate-900/30 -ml-3 pl-3 pr-2 py-1 rounded transition-colors' : ''}`}
        onClick={() => hasContent && setIsExpanded(!isExpanded)}
      >
        {/* Expand icon for steps with content */}
        {hasContent && (
          <span className="text-gray-600">
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </span>
        )}
        
        {status === 'completed' ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
        ) : status === 'in_progress' ? (
          <Loader2 className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 animate-spin" />
        ) : (
          <Circle className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
        )}
        <span className={`text-xs flex-1 ${status === 'completed' ? 'text-gray-300' : status === 'in_progress' ? 'text-gray-200' : 'text-gray-500'}`}>
          {step.name}
        </span>
        {step.duration && status === 'completed' && (
          <span className="text-[10px] text-gray-600 font-mono">{step.duration}</span>
        )}
      </div>

      {/* Step content */}
      {hasContent && isExpanded && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="ml-6 mt-2"
        >
          {step.hasQuery ? (
            <div>
              <div className="flex gap-0.5 mb-2 bg-slate-900/60 rounded-lg p-0.5 w-fit">
                <button
                  onClick={(e) => { e.stopPropagation(); setActiveTab('query'); }}
                  className={`text-[10px] px-2.5 py-1 rounded flex items-center gap-1 ${activeTab === 'query' ? 'bg-slate-800 text-gray-200' : 'text-gray-500'}`}
                >
                  <Database className="w-3 h-3" /> Query
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setActiveTab('data'); }}
                  className={`text-[10px] px-2.5 py-1 rounded flex items-center gap-1 ${activeTab === 'data' ? 'bg-slate-800 text-gray-200' : 'text-gray-500'}`}
                >
                  <Table2 className="w-3 h-3" /> Data
                </button>
              </div>
              {activeTab === 'query' && (
                <div className="relative group">
                  <CopyButton text={step.query} />
                  <pre className="p-2.5 rounded-lg bg-slate-900/60 border border-slate-800/50 overflow-x-auto text-[10px] font-mono text-gray-400 leading-relaxed">
                    <code>{step.query}</code>
                  </pre>
                </div>
              )}
              {activeTab === 'data' && <DataTable data={step.data} />}
            </div>
          ) : step.output && (
            <p className="text-xs text-gray-500 bg-slate-900/40 px-2.5 py-2 rounded-lg">{step.output}</p>
          )}
        </motion.div>
      )}
    </div>
  );
};

export const QueryAgentCard = ({ 
  subAgent, 
  isActive = false,
  onComplete,
  onStepChange,
  forceCollapse = false,
  onExpandChange,
  onPublish
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [visibleSteps, setVisibleSteps] = useState(0);
  const [stepStatuses, setStepStatuses] = useState(subAgent.steps.map(() => 'pending'));
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [showResponse, setShowResponse] = useState(false);
  
  const animationStarted = useRef(false);
  const completionTriggered = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const onStepChangeRef = useRef(onStepChange);
  const onExpandChangeRef = useRef(onExpandChange);

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onStepChangeRef.current = onStepChange;
    onExpandChangeRef.current = onExpandChange;
  }, [onComplete, onStepChange, onExpandChange]);

  // Auto-collapse when forceCollapse is true
  useEffect(() => {
    if (forceCollapse) {
      setIsExpanded(false);
    }
  }, [forceCollapse]);

  // Notify parent of expand state changes
  useEffect(() => {
    if (onExpandChangeRef.current) {
      onExpandChangeRef.current(isExpanded);
    }
  }, [isExpanded]);

  // Run step animations with variable timing based on step type
  useEffect(() => {
    if (!isActive || animationStarted.current) return;
    animationStarted.current = true;

    let cumulativeDelay = 0;

    subAgent.steps.forEach((step, stepIndex) => {
      const stepDelay = cumulativeDelay;
      // Query steps take much longer (8s), non-query steps (3s)
      const stepDuration = step.hasQuery ? 8000 : 3000;

      // Show step as in_progress
      setTimeout(() => {
        setVisibleSteps(stepIndex + 1);
        setCurrentStepIndex(stepIndex);
        setStepStatuses(prev => {
          const updated = [...prev];
          updated[stepIndex] = 'in_progress';
          return updated;
        });
        if (onStepChangeRef.current) onStepChangeRef.current();
      }, stepDelay);

      // Mark step as complete
      setTimeout(() => {
        setStepStatuses(prev => {
          const updated = [...prev];
          updated[stepIndex] = 'completed';
          return updated;
        });
        if (onStepChangeRef.current) onStepChangeRef.current();

        // After last step, show response and complete
        if (stepIndex === subAgent.steps.length - 1 && !completionTriggered.current) {
          completionTriggered.current = true;
          setCurrentStepIndex(-1);
          setTimeout(() => {
            setShowResponse(true);
            if (onStepChangeRef.current) onStepChangeRef.current();
            setTimeout(() => {
              if (onCompleteRef.current) onCompleteRef.current();
            }, 500);
          }, 400);
        }
      }, stepDelay + stepDuration);

      cumulativeDelay += stepDuration + 500; // Add gap between steps
    });
  }, [isActive, subAgent.steps]);

  const allStepsComplete = stepStatuses.every(s => s === 'completed');
  
  // Determine current status text based on active step
  const getCurrentStatusText = () => {
    if (currentStepIndex >= 0 && currentStepIndex < subAgent.steps.length) {
      const currentStep = subAgent.steps[currentStepIndex];
      return currentStep.hasQuery ? 'Executing query' : 'Processing';
    }
    return 'Processing';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="mb-3"
      data-testid={`query-agent-card-${subAgent.id}`}
    >
      <div className="rounded-xl bg-slate-950/50 overflow-hidden">
        {/* Header - Clickable */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full text-left p-4 flex items-center justify-between hover:bg-slate-900/30 transition-colors"
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-gray-500">
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-gray-300">{subAgent.name}</span>
              {!isExpanded && (
                <p className="text-[10px] text-gray-500 mt-0.5 truncate">{subAgent.description}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {allStepsComplete ? (
              <>
                <span className="flex items-center gap-1 text-[10px] text-gray-500">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  {subAgent.steps.length} steps
                </span>
                {subAgent.duration && (
                  <span className="flex items-center gap-1 text-[10px] text-gray-500 font-mono">
                    <Clock className="w-3 h-3" />{subAgent.duration}
                  </span>
                )}
              </>
            ) : isActive ? (
              <span className="flex items-center gap-1.5 text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full">
                <Loader2 className="w-3 h-3 animate-spin" /> {getCurrentStatusText()}
              </span>
            ) : null}
          </div>
        </button>

        {/* Content */}
        {isExpanded && (
          <div className="px-4 pb-4">
            <p className="text-[10px] text-gray-500 mb-3">{subAgent.description}</p>
            
            {/* Steps */}
            <div className="space-y-0.5">
              {subAgent.steps.slice(0, visibleSteps).map((step, idx) => (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <StepItem step={step} status={stepStatuses[idx]} forceCollapse={forceCollapse} />
                </motion.div>
              ))}
            </div>

            {/* Response - only show simple text responses inside card */}
            {showResponse && subAgent.response && typeof subAgent.response === 'string' && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-3 pt-3 border-t border-slate-800/50"
              >
                <p className="text-xs text-gray-400">{subAgent.response}</p>
              </motion.div>
            )}
          </div>
        )}
      </div>
      
      {/* Rich Response - rendered outside collapsible area so it stays visible */}
      {showResponse && subAgent.response && typeof subAgent.response === 'object' && (
        <AgentResponse response={subAgent.response} agentName={subAgent.name} onPublish={onPublish} />
      )}
    </motion.div>
  );
};

export default QueryAgentCard;
